import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2, Clock, IndianRupee, Users, TrendingUp, BarChart3,
  ArrowDown, ArrowUp, RefreshCw, Timer, Target, Zap, Award, ChevronRight, AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, Legend
} from "recharts";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { DateRangePicker, openLeadsTab } from "../components/DateRangePicker";
import { appendDateParams } from "../utils/dateFilters";
import { InfoTip } from "../components/InfoTip";
import { last30Days } from "../lib/dateDefaults";

const _DEF = last30Days();

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const STAGE_COLORS = {
  new: "#3B82F6", contacted: "#6366F1", qualified: "#8B5CF6",
  site_visit_scheduled: "#B45309", site_visit: "#D97706", revisit: "#F59E0B", negotiation_scheduled: "#047857", negotiation: "#059669",
  eoi: "#7C3AED", booked: "#10B981", re_engaged: "#F97316", lost: "#EF4444",
};

const ControlTowerPage = () => {
  const [tab, setTab] = useState("funnels");
  const [funnels, setFunnels] = useState([]);
  const [stageData, setStageData] = useState(null);
  const [responseTimes, setResponseTimes] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState({});
  const [dateFrom, setDateFrom] = useState(_DEF.from);
  const [dateTo, setDateTo] = useState(_DEF.to);

  const load = async (key, url, setter) => {
    setLoading((p) => ({ ...p, [key]: true }));
    try {
      const dp = new URLSearchParams();
      appendDateParams(dp, dateFrom, dateTo);
      const qs = dp.toString();
      const res = await axios.get(`${API}/control-tower/${url}${qs ? `?${qs}` : ""}`);
      setter(res.data);
    } catch { toast.error(`Failed to load ${key}`); }
    finally { setLoading((p) => ({ ...p, [key]: false })); }
  };

  useEffect(() => { load("funnels", "project-funnels", setFunnels); }, [dateFrom, dateTo]);

  useEffect(() => {
    if (tab === "stages") load("stages", "stage-analytics", setStageData);
    if (tab === "response") load("response", "response-times", setResponseTimes);
    if (tab === "revenue") load("revenue", "revenue-projection", setRevenue);
    if (tab === "team") load("team", "team-scorecard", setTeamData);
  }, [tab, dateFrom, dateTo]);

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="control-tower-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Control Tower</h1>
            <p className="text-sm text-slate-500 mt-1">Real-time analytics, response tracking & revenue projections</p>
          </div>
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1" data-testid="control-tower-tabs">
            <TabsTrigger value="funnels" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Funnels</TabsTrigger>
            <TabsTrigger value="stages" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Stages</TabsTrigger>
            <TabsTrigger value="response" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Response</TabsTrigger>
            <TabsTrigger value="revenue" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Revenue</TabsTrigger>
            <TabsTrigger value="team" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Team</TabsTrigger>
            <InfoTip text="Control Tower gives you a bird's-eye view. Funnels = leads per project. Stage Analytics = how long leads stay in each stage. Response Times = how fast reps respond. Revenue = projected pipeline value. Team Scorecard = rep performance rankings." />
          </TabsList>

          {/* ===== PROJECT FUNNELS ===== */}
          <TabsContent value="funnels" className="space-y-4 mt-4">
            {loading.funnels ? <LoadingBlock /> : funnels.length === 0 ? (
              <EmptyBlock text="No project funnel data yet. Assign leads to projects to see funnels." />
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard icon={Building2} label="Projects" value={funnels.length} color="bg-blue-100 text-blue-700" />
                  <MetricCard icon={Users} label="Total Leads" value={funnels.reduce((s, f) => s + f.total_leads, 0)} color="bg-slate-100 text-slate-700" onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })} />
                  <MetricCard icon={Target} label="Total Booked" value={funnels.reduce((s, f) => s + f.booked, 0)} color="bg-emerald-100 text-emerald-700" onClick={() => openLeadsTab({ stage: "booked", date_from: dateFrom, date_to: dateTo })} />
                  <MetricCard icon={TrendingUp} label="Avg Conversion"
                    value={`${(funnels.reduce((s, f) => s + f.conversion_rate, 0) / Math.max(funnels.length, 1)).toFixed(1)}%`}
                    color="bg-amber-100 text-amber-700" />
                </div>

                {/* Per-project funnel cards */}
                <div className="space-y-3">
                  {funnels.map((f) => (
                    <Card key={f.project_id} data-testid={`funnel-card-${f.project_id}`} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ project: f.project_name, date_from: dateFrom, date_to: dateTo })}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-500" />
                            <span className="font-semibold text-slate-900">{f.project_name}</span>
                            <Badge variant="secondary" className="text-xs">{f.total_leads} leads</Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-emerald-600 font-medium">{f.booked} booked</span>
                            <span className="text-red-500 font-medium">{f.lost} lost</span>
                            <Badge className={`${f.conversion_rate >= 10 ? "bg-emerald-500" : f.conversion_rate >= 5 ? "bg-amber-500" : "bg-slate-400"} text-white`}>
                              {f.conversion_rate}%
                            </Badge>
                          </div>
                        </div>
                        {/* Stage bar */}
                        <div className="flex h-7 rounded-lg overflow-hidden bg-slate-100">
                          {Object.entries(f.stages).map(([stage, count]) => {
                            const pct = (count / Math.max(f.total_leads, 1)) * 100;
                            if (pct < 1) return null;
                            return (
                              <div key={stage} style={{ width: `${pct}%`, backgroundColor: STAGE_COLORS[stage] }}
                                className="flex items-center justify-center transition-all" title={`${stage}: ${count}`}>
                                {pct > 8 && <span className="text-[10px] font-bold text-white truncate px-1">{stage.replace(/_/g, " ")}</span>}
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                          {Object.entries(f.stages).map(([stage, count]) => (
                            <span key={stage} className="text-xs text-slate-500 flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                              {stage.replace(/_/g, " ")}: {count}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* ===== STAGE ANALYTICS ===== */}
          <TabsContent value="stages" className="space-y-4 mt-4">
            {loading.stages ? <LoadingBlock /> : !stageData ? <EmptyBlock text="No stage data." /> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <MetricCard icon={Users} label="Total Leads" value={stageData.total_leads} color="bg-blue-100 text-blue-700" onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })} />
                  <MetricCard icon={Zap} label="Weekly Velocity" value={`${stageData.weekly_velocity} leads/wk`} color="bg-amber-100 text-amber-700" />
                  <MetricCard icon={TrendingUp} label="Active Stages" value={stageData.stages.filter(s => s.count > 0).length} color="bg-emerald-100 text-emerald-700" />
                </div>

                {/* Stage breakdown chart */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Stage Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={stageData.stages} layout="vertical" onClick={(e) => { if (e?.activePayload?.[0]) openLeadsTab({ stage: e.activePayload[0].payload.stage, date_from: dateFrom, date_to: dateTo }); }} style={{ cursor: "pointer" }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis type="number" />
                        <YAxis dataKey="stage" type="category" width={85} tick={{ fontSize: 12 }}
                          tickFormatter={(v) => v.replace(/_/g, " ")} />
                        <Tooltip formatter={(v) => [v, "Leads"]}
                          contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px" }} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {stageData.stages.map((s) => (
                            <Cell key={s.stage} fill={STAGE_COLORS[s.stage] || "#94A3B8"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Drop-off table */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Stage Drop-off & Velocity</CardTitle>
                    <p className="text-xs text-slate-400">Avg Days = average time a lead spends in this stage. Drop-off = % of leads that left the funnel at this stage.</p>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-slate-500">
                            <th className="text-left py-2 font-medium">Stage</th>
                            <th className="text-right py-2 font-medium">Leads</th>
                            <th className="text-right py-2 font-medium">% of Total</th>
                            <th className="text-right py-2 font-medium">Drop-off</th>
                            <th className="text-right py-2 font-medium">Avg Hours</th>
                            <th className="text-right py-2 font-medium">Transitions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stageData.stages.map((s) => (
                            <tr key={s.stage} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => openLeadsTab({ stage: s.stage, date_from: dateFrom, date_to: dateTo })}>
                              <td className="py-2 flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STAGE_COLORS[s.stage] }} />
                                <span className="capitalize font-medium text-slate-800">{s.stage.replace(/_/g, " ")}</span>
                              </td>
                              <td className="text-right font-semibold text-slate-900">{s.count}</td>
                              <td className="text-right text-slate-600">{s.pct_of_total}%</td>
                              <td className="text-right">
                                {s.drop_off_pct > 0 ? (
                                  <span className={`flex items-center justify-end gap-0.5 ${s.drop_off_pct > 50 ? "text-red-600" : s.drop_off_pct > 25 ? "text-amber-600" : "text-slate-500"}`}>
                                    <ArrowDown className="w-3 h-3" />{s.drop_off_pct}%
                                  </span>
                                ) : <span className="text-slate-400">—</span>}
                              </td>
                              <td className="text-right text-slate-600">{s.avg_hours_in_stage != null ? `${s.avg_hours_in_stage}h` : "—"}</td>
                              <td className="text-right text-slate-600">{s.transitions_out || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Weekly Trend */}
                {stageData.weekly_trend?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Weekly New Leads</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={stageData.weekly_trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                          <YAxis />
                          <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px" }} />
                          <Bar dataKey="new_leads" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ===== RESPONSE TIMES ===== */}
          <TabsContent value="response" className="space-y-4 mt-4">
            {loading.response ? <LoadingBlock /> : !responseTimes ? <EmptyBlock text="No response time data." /> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard icon={Clock} label="Avg Response"
                    value={responseTimes.avg_response_hours != null ? `${responseTimes.avg_response_hours}h` : "N/A"}
                    color="bg-blue-100 text-blue-700" />
                  <MetricCard icon={Timer} label="Median Response"
                    value={responseTimes.median_response_hours != null ? `${responseTimes.median_response_hours}h` : "N/A"}
                    color="bg-slate-100 text-slate-700" />
                  <MetricCard icon={Target} label="SLA Compliance (2h)"
                    value={`${responseTimes.sla_2h_compliance_pct}%`}
                    color={`${responseTimes.sla_2h_compliance_pct >= 80 ? "bg-emerald-100 text-emerald-700" : responseTimes.sla_2h_compliance_pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`} />
                  <MetricCard icon={Building2} label="Avg Days to Visit"
                    value={responseTimes.avg_days_to_site_visit != null ? `${responseTimes.avg_days_to_site_visit}d` : "N/A"}
                    color="bg-violet-100 text-violet-700" />
                </div>

                {/* By source */}
                {responseTimes.by_source?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Response Time by Source</CardTitle>
                      <p className="text-xs text-slate-400">Average hours between lead creation and first interaction, grouped by source. Lower = better responsiveness.</p>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={Math.max(responseTimes.by_source.length * 40, 150)}>
                        <BarChart data={responseTimes.by_source} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis type="number" unit="h" />
                          <YAxis dataKey="source" type="category" width={100} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v) => [`${v}h`, "Avg Response"]}
                            contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px" }} />
                          <Bar dataKey="avg_hours" fill="#6366F1" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* By rep */}
                {responseTimes.by_rep?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Response Time by Rep</CardTitle>
                      <p className="text-xs text-slate-400">Average first-response time per sales rep. Reps with slower response times may need workload rebalancing.</p>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-slate-500">
                              <th className="text-left py-2 font-medium">Rep</th>
                              <th className="text-right py-2 font-medium">Avg Response (h)</th>
                              <th className="text-right py-2 font-medium">Leads Tracked</th>
                              <th className="py-2 font-medium w-40"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {responseTimes.by_rep.map((r) => (
                              <tr key={r.user_id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => openLeadsTab({ assigned_to: r.user_id, date_from: dateFrom, date_to: dateTo })}>
                                <td className="py-2 font-medium text-slate-800">{r.name}</td>
                                <td className="text-right">
                                  <span className={`font-semibold ${r.avg_hours <= 2 ? "text-emerald-600" : r.avg_hours <= 6 ? "text-amber-600" : "text-red-600"}`}>
                                    {r.avg_hours}h
                                  </span>
                                </td>
                                <td className="text-right text-slate-600">{r.count}</td>
                                <td className="py-2">
                                  <Progress value={Math.min(100 - (r.avg_hours / 24) * 100, 100)} className="h-1.5" />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ===== REVENUE PROJECTION ===== */}
          <TabsContent value="revenue" className="space-y-4 mt-4">
            {loading.revenue ? <LoadingBlock /> : !revenue ? <EmptyBlock text="No revenue data." /> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white cursor-pointer hover:from-slate-800 hover:to-slate-700 transition-all" onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })}>
                    <CardContent className="p-5 text-center">
                      <p className="text-xs text-slate-300">Total Pipeline Value</p>
                      <p className="text-2xl font-bold font-['Manrope'] mt-1" data-testid="total-pipeline-value">{formatINR(revenue.total_pipeline_value)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white cursor-pointer hover:from-emerald-500 hover:to-emerald-600 transition-all" onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })}>
                    <CardContent className="p-5 text-center">
                      <p className="text-xs text-emerald-100">Weighted Forecast</p>
                      <p className="text-2xl font-bold font-['Manrope'] mt-1" data-testid="weighted-forecast">{formatINR(revenue.total_weighted_value)}</p>
                    </CardContent>
                  </Card>
                  <MetricCard icon={Users} label="Active Leads" value={revenue.total_leads} color="bg-blue-100 text-blue-700" />
                </div>

                {/* Missing budget warning */}
                {revenue.leads_missing_budget > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="missing-budget-warning">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        {revenue.leads_missing_budget} of {revenue.total_leads} leads have no budget data
                      </p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        Pipeline values for these leads are estimated from project averages of leads with known budgets. Update lead budgets for more accurate forecasting.
                      </p>
                    </div>
                  </div>
                )}

                {/* By stage */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Pipeline by Stage</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={revenue.by_stage} onClick={(e) => { if (e?.activePayload?.[0]) openLeadsTab({ stage: e.activePayload[0].payload.stage, date_from: dateFrom, date_to: dateTo }); }} style={{ cursor: "pointer" }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="stage" tick={{ fontSize: 11 }} tickFormatter={(v) => v.replace(/_/g, " ")} />
                        <YAxis tickFormatter={(v) => formatINR(v)} />
                        <Tooltip formatter={(v, name) => [formatINR(v), name === "pipeline_value" ? "Pipeline" : "Weighted"]}
                          contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px" }} />
                        <Legend />
                        <Bar dataKey="pipeline_value" name="Pipeline" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="weighted_value" name="Weighted" fill="#10B981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* By project table */}
                {revenue.by_project?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Revenue by Project</CardTitle></CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-slate-500">
                              <th className="text-left py-2 font-medium">Project</th>
                              <th className="text-right py-2 font-medium">Leads</th>
                              <th className="text-right py-2 font-medium">Pipeline Value</th>
                              <th className="text-right py-2 font-medium">Weighted Forecast</th>
                              <th className="text-right py-2 font-medium">Missing Budget</th>
                            </tr>
                          </thead>
                          <tbody>
                            {revenue.by_project.map((p) => (
                              <tr key={p.project} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => openLeadsTab({ project: p.project, date_from: dateFrom, date_to: dateTo })}>
                                <td className="py-2 font-medium text-slate-800">{p.project}</td>
                                <td className="text-right text-slate-600">{p.count}</td>
                                <td className="text-right font-semibold text-slate-900">{formatINR(p.pipeline_value)}</td>
                                <td className="text-right font-semibold text-emerald-600">{formatINR(p.weighted_value)}</td>
                                <td className="text-right">{p.missing_budget > 0 ? <span className="text-amber-600 text-xs font-medium">{p.missing_budget} leads</span> : <span className="text-slate-300">—</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ===== TEAM SCORECARD ===== */}
          <TabsContent value="team" className="space-y-4 mt-4">
            {loading.team ? <LoadingBlock /> : !teamData ? <EmptyBlock text="No team data." /> : (
              <>
                {/* Team-level */}
                {teamData.teams?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Team Performance</CardTitle>
                      <p className="text-xs text-slate-400">Conversion rate and lead volume per team member. Click to view their assigned leads.</p>
                    </CardHeader>
                    <CardContent>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {teamData.teams.map((t) => (
                          <div key={t.team_id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow" data-testid={`team-card-${t.team_id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-slate-900">{t.team_name}</span>
                              <Badge variant="outline" className="text-xs capitalize">{t.team_type}</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center text-xs">
                              <div><p className="text-slate-500">Members</p><p className="font-bold text-lg text-slate-900">{t.member_count}</p></div>
                              <div><p className="text-slate-500">Leads</p><p className="font-bold text-lg text-slate-900">{t.total_leads}</p></div>
                              <div><p className="text-slate-500">Conversion</p><p className={`font-bold text-lg ${t.conversion_rate >= 10 ? "text-emerald-600" : "text-amber-600"}`}>{t.conversion_rate}%</p></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Rep-level table */}
                {teamData.reps?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Individual Rep Scorecard</CardTitle>
                      <p className="text-xs text-slate-400">Detailed metrics per rep: total leads, booked, lost, active pipeline, visits, and tasks. Use this to identify coaching opportunities.</p>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-slate-500">
                              <th className="text-left py-2 font-medium">Rep</th>
                              <th className="text-right py-2 font-medium">Leads</th>
                              <th className="text-right py-2 font-medium">Active</th>
                              <th className="text-right py-2 font-medium">Booked</th>
                              <th className="text-right py-2 font-medium">Lost</th>
                              <th className="text-right py-2 font-medium">Conversion</th>
                              <th className="text-right py-2 font-medium">Tasks Done</th>
                              <th className="text-right py-2 font-medium">Visits</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teamData.reps.map((r) => (
                              <tr key={r.user_id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`rep-row-${r.user_id}`} onClick={() => openLeadsTab({ assigned_to: r.user_id, date_from: dateFrom, date_to: dateTo })}>
                                <td className="py-2">
                                  <span className="font-medium text-slate-800">{r.name}</span>
                                  <span className="text-xs text-slate-400 ml-1 capitalize">({r.role})</span>
                                </td>
                                <td className="text-right font-semibold text-slate-900">{r.total_leads}</td>
                                <td className="text-right text-blue-600">{r.active_leads}</td>
                                <td className="text-right text-emerald-600 font-medium">{r.booked}</td>
                                <td className="text-right text-red-500">{r.lost}</td>
                                <td className="text-right">
                                  <Badge className={`${r.conversion_rate >= 15 ? "bg-emerald-500" : r.conversion_rate >= 5 ? "bg-amber-500" : "bg-slate-400"} text-white text-xs`}>
                                    {r.conversion_rate}%
                                  </Badge>
                                </td>
                                <td className="text-right text-slate-600">{r.tasks_completed}/{r.tasks_total}</td>
                                <td className="text-right text-slate-600">{r.site_visits}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

/* --- Helper Components --- */
const MetricCard = ({ icon: Icon, label, value, color, onClick }) => (
  <Card className={onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""} onClick={onClick}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-xs text-slate-500">{label}</p><p className="text-lg font-bold text-slate-900 font-['Manrope']">{value}</p></div>
    </CardContent>
  </Card>
);

const LoadingBlock = () => (
  <div className="py-16 text-center text-slate-400">
    <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin opacity-50" />
    <p>Loading...</p>
  </div>
);

const EmptyBlock = ({ text }) => (
  <Card><CardContent className="p-12 text-center text-slate-400"><BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>{text}</p></CardContent></Card>
);

export default ControlTowerPage;
